"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "Initial Visit",
  description: "Review of symptoms, medical history, medications, prior testing, and current breathing or sleep-related concerns.",
  timing: "FIRST VISIT"
}, {
  number: "02",
  title: "Evaluation & Testing Review",
  description: "Review of available test results, sleep study information, imaging reports, pulmonary history, and related clinical findings when applicable.",
  timing: "AS NEEDED"
}, {
  number: "03",
  title: "Care Plan Discussion",
  description: "Discussion of diagnosis, treatment options, medication use, CPAP support, oxygen needs, referrals, or follow‑up recommendations when appropriate.",
  timing: "CARE PLANNING"
}, {
  number: "04",
  title: "Follow‑Up Care",
  description: "Ongoing pulmonary or sleep medicine follow‑up based on symptoms, condition history, treatment response, and clinical needs.",
  timing: "ONGOING CARE"
}];
function TimelineStep({
  step,
  index
}: {
  step: (typeof steps)[number];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} className="relative transition-all duration-700 ease-out" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)",
    transitionDelay: `${index * 100}ms`
  }}>
      {}
      <div className="hidden md:flex flex-col items-start">
        <div className="font-default text-[88px] leading-none text-primary tracking-tighter">
          <span>{step.number.charAt(0)}</span>
          <span className="text-teal-muted">{step.number.charAt(1)}</span>
        </div>

        {}
        <div className="relative w-full h-8 flex items-center mt-8 mb-8">
          <div className="absolute left-0 w-3 h-3 rounded-md bg-teal-muted" />
        </div>

        <h3 className="font-default text-[28px] leading-tight text-foreground mb-4 font-semibold">
          {step.title}
        </h3>
        <p className="font-sans text-[15px] leading-relaxed text-slate-text mb-4">
          {step.description}
        </p>
        <p className="font-mono text-xs uppercase tracking-widest text-teal-muted">{step.timing}</p>
      </div>

      {}
      <div className="md:hidden flex gap-6 pl-2">
        <div className="flex flex-col items-center shrink-0 pt-2">
          <div className="w-3 h-3 rounded-md bg-teal-muted shrink-0" />
        </div>
        <div className="flex-1 pb-12">
          <div className="font-default text-[56px] leading-none text-primary tracking-tighter mb-4">
            <span>{step.number.charAt(0)}</span>
            <span className="text-teal-muted">{step.number.charAt(1)}</span>
          </div>
          <h3 className="font-default text-2xl leading-tight text-foreground mb-3 font-semibold">
            {step.title}
          </h3>
          <p className="font-sans text-[15px] leading-relaxed text-slate-text mb-3">
            {step.description}
          </p>
          <p className="font-mono text-xs uppercase tracking-widest text-teal-muted">{step.timing}</p>
        </div>
      </div>
    </div>;
}
export default function ThePathToBetterHealth() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="38" id="the-path-to-better-health" className="bg-background text-foreground py-24 md:py-[140px] px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className="max-w-3xl mb-20 md:mb-28 transition-all duration-700 ease-out" style={{
        opacity: headerVisible ? 1 : 0,
        transform: headerVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <p className="font-mono text-xs tracking-widest text-teal-muted uppercase mb-8">
            — 02 / WHAT TO EXPECT
          </p>
          <h2 className="font-default text-4xl md:text-5xl lg:text-[48px] leading-[1.05] text-foreground tracking-tight mb-8 font-medium">
            What to Expect During Care
          </h2>
          <p className="font-sans text-[15px] md:text-base leading-relaxed text-slate-text max-w-xl">
            From the first visit to follow-up care, Chaudhry Pulmonary Associates focuses on careful evaluation, clear communication, and practical next steps for adults with pulmonary and sleep-related concerns.
          </p>
        </div>

        {}
        <div className="hidden md:block relative">
          {}
          <div className="absolute left-0 right-0 h-px bg-border pointer-events-none" style={{
          top: "calc(88px + 2rem + 0.625rem)"
        }} aria-hidden="true" />
          <div className="grid grid-cols-4 gap-8 lg:gap-12">
            {steps.map((step, i) => <TimelineStep key={step.number} step={step} index={i} />)}
          </div>
        </div>

        {}
        <div className="md:hidden relative">
          {}
          <div className="absolute left-3 top-0 bottom-0 w-px bg-border pointer-events-none" aria-hidden="true" />
          <div className="flex flex-col">
            {steps.map((step, i) => <TimelineStep key={step.number} step={step} index={i} />)}
          </div>
        </div>

        {}
        <div ref={footerRef} className="mt-20 md:mt-32 transition-all duration-700 ease-out" style={{
        opacity: footerVisible ? 1 : 0,
        transform: footerVisible ? "translateY(0)" : "translateY(24px)"
      }}>
          <Separator className="mb-12 bg-border" />
          <div className="flex justify-center">
            <Link to="/PatientResourcesPage" className="inline-flex items-center gap-2 font-default text-base text-primary group font-medium">
              <span className="underline decoration-teal-muted decoration-1 underline-offset-[6px] group-hover:decoration-2 transition-all">
                View our patient information guide
              </span>
              <ArrowRight className="size-4 text-teal-muted transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>;
}